/**
 * @fileoverview Approvals API service.
 * @description Calls the backend's prospect review endpoints (Confluence 3.5.12): the review list
 * (GET /approvals, every page at once), its Pending Review Summary (GET /approvals/summary), one
 * request in full (GET /approvals/{id}), Approve and Reject, and Bulk Approve (POST
 * /approvals/bulk-approve). The single commands send the request's `approvalVersion` back as `X-Approval-Version`;
 * every command takes its `Idempotency-Key` from the caller (see useIdempotentMutation), so a retry
 * resends the same key. Bulk Approve sends each item's version in its body.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { readAllItems, withoutBlankFilters } from '@/features/sales/services/prospects.service'
import { httpClient } from '@/shared/api/http-client'

import type {
  ApprovalDecisionResult,
  ApprovalDetail,
  ApprovalListFilters,
  ApprovalListItem,
  ApprovalSummary,
  BulkApproveItem,
  BulkApproveResult,
  RejectApprovalRequest,
} from '@/features/sales/types/approval.types'

/**
 * @description Fetches every review request matching the filters, page by page (see readAllItems).
 * @param {ApprovalListFilters} [filters] - Whose requests, and in which state.
 * @returns {Promise<ApprovalListItem[]>} Every matching request, newest submission first.
 */
export function getAllApprovalRequests(
  filters: ApprovalListFilters = {},
): Promise<ApprovalListItem[]> {
  return readAllItems<ApprovalListItem>('/approvals', withoutBlankFilters(filters))
}

/**
 * @description Fetches one review request in full, including the version a decision needs.
 * @param {string} approvalRequestId - The request to fetch.
 * @returns {Promise<ApprovalDetail>} The request, its subject and its history.
 */
export async function getApprovalRequest(approvalRequestId: string): Promise<ApprovalDetail> {
  const { data } = await httpClient.get<ApprovalDetail>(
    `/approvals/${encodeURIComponent(approvalRequestId)}`,
  )
  return data
}

/**
 * @description Approves a pending review request, activating its proposed owner.
 * @param {string} approvalRequestId - The request to approve.
 * @param {string} approvalVersion - Its current version, sent as X-Approval-Version.
 * @param {string | null} reviewNote - An optional note.
 * @param {string} idempotencyKey - The command's key; resend the same key to retry it safely.
 * @returns {Promise<ApprovalDecisionResult>} The decision.
 */
export async function approveApprovalRequest(
  approvalRequestId: string,
  approvalVersion: string,
  reviewNote: string | null,
  idempotencyKey: string,
): Promise<ApprovalDecisionResult> {
  const { data } = await httpClient.post<ApprovalDecisionResult>(
    `/approvals/${encodeURIComponent(approvalRequestId)}/approve`,
    reviewNote ? { reviewNote } : {},
    { headers: { 'X-Approval-Version': approvalVersion, 'Idempotency-Key': idempotencyKey } },
  )
  return data
}

/**
 * @description Rejects a pending review request.
 * @param {string} approvalRequestId - The request to reject.
 * @param {string} approvalVersion - Its current version, sent as X-Approval-Version.
 * @param {RejectApprovalRequest} request - The reason code and review note.
 * @param {string} idempotencyKey - The command's key; resend the same key to retry it safely.
 * @returns {Promise<ApprovalDecisionResult>} The decision.
 */
export async function rejectApprovalRequest(
  approvalRequestId: string,
  approvalVersion: string,
  request: RejectApprovalRequest,
  idempotencyKey: string,
): Promise<ApprovalDecisionResult> {
  const { data } = await httpClient.post<ApprovalDecisionResult>(
    `/approvals/${encodeURIComponent(approvalRequestId)}/reject`,
    request,
    { headers: { 'X-Approval-Version': approvalVersion, 'Idempotency-Key': idempotencyKey } },
  )
  return data
}

/**
 * @description Fetches the Pending Review Summary: every pending prospect request in the
 * organisation, by who submitted it and how hot the lead is. Only a Super Admin or a Sales Head may
 * read it (anyone else is refused `403`).
 * @returns {Promise<ApprovalSummary>} The counters.
 */
export async function getApprovalSummary(): Promise<ApprovalSummary> {
  const { data } = await httpClient.get<ApprovalSummary>('/approvals/summary')
  return data
}

/**
 * @description Approves up to 50 pending requests at once. Each is decided on its own, so a request
 * that changed since it was read is skipped (with the code Approve would have answered) while the
 * rest are approved.
 * @param {BulkApproveItem[]} items - The requests, each with the version it was read at.
 * @param {string | null} reviewNote - An optional note recorded on every decision.
 * @param {string} idempotencyKey - The command's key; resend the same key to retry it safely.
 * @returns {Promise<BulkApproveResult>} The approved and the skipped.
 */
export async function bulkApproveApprovalRequests(
  items: BulkApproveItem[],
  reviewNote: string | null,
  idempotencyKey: string,
): Promise<BulkApproveResult> {
  const { data } = await httpClient.post<BulkApproveResult>(
    '/approvals/bulk-approve',
    reviewNote ? { items, reviewNote } : { items },
    { headers: { 'Idempotency-Key': idempotencyKey } },
  )
  return data
}
